import { useState, useEffect } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import {
  Card,
  Table,
  Stack,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Box,
  CardHeader,
  CardContent,
  Paper,
  Chip,
  Alert,
  TextField,
  InputAdornment,
  Container,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { ThreeDots } from 'react-loader-spinner';
import Scrollbar from '../../../components/scrollbar';
import { UserListHead } from '../../../sections/@dashboard/user';
import { Page } from '../../../components';
import { getDepartments, resetDepartmentState } from '../store/departmentActions';
import { DepartmentForm, DepartmentEditForm } from '../components';
import { fDate } from '../../../utils/formatTime';

const TABLE_HEAD = [
  { id: 'name', label: 'Department Name', alignRight: false },
  { id: 'code', label: 'Code', alignRight: false },
  { id: 'is_active', label: 'Status', alignRight: false },
  { id: 'date_created', label: 'Created Date', alignRight: false },
  { id: 'actions', label: 'Actions', alignRight: false },
];

export default function Departments() {
  const dispatch = useDispatch();
  const { isLoading, departments, pagination, isSuccess } = useSelector((state) => state.departmentReducer);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);

  const fetchDepartments = () => {
    dispatch(getDepartments({ page: page + 1, rowsPerPage }));
  };

  useEffect(() => {
    fetchDepartments();
  }, [dispatch, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isSuccess) {
      fetchDepartments();
      dispatch(resetDepartmentState());
    }
  }, [isSuccess]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditClick = (dept) => {
    setSelectedDepartment(dept);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedDepartment(null);
  };

  const filteredDepartments =
    departments?.filter(
      (d) =>
        d.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        d.code?.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];

  return (
    <Page title="Departments">
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h5">Departments</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenAddModal(true)} sx={{ borderRadius: 2 }}>
              Add Department
            </Button>
          </Stack>

          <Card>
            <CardHeader
              title="Departments List"
              subheader={`${pagination?.count || 0} total`}
              action={
                <TextField
                  size="small"
                  placeholder="Search departments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 240 }}
                />
              }
            />
            <CardContent>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 700 }} component={Paper}>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                      <ThreeDots height="80" width="80" radius="9" color="#2065d1" ariaLabel="three-dots-loading" visible />
                    </Box>
                  ) : (
                    <Table size="medium">
                      <UserListHead headLabel={TABLE_HEAD} rowCount={filteredDepartments.length} />
                      <TableBody>
                        {filteredDepartments.length > 0 ? (
                          filteredDepartments.map((dept) => (
                            <TableRow hover key={dept.id} tabIndex={-1}>
                              <TableCell>
                                <Typography variant="subtitle2" noWrap>
                                  {dept.name}
                                </Typography>
                              </TableCell>

                              <TableCell>
                                {dept.code ? (
                                  <Chip label={dept.code} size="small" variant="outlined" color="primary" />
                                ) : (
                                  <Typography variant="body2" color="text.secondary">—</Typography>
                                )}
                              </TableCell>

                              <TableCell>
                                <Chip
                                  label={dept.is_active ? 'Active' : 'Inactive'}
                                  size="small"
                                  color={dept.is_active ? 'success' : 'default'}
                                />
                              </TableCell>

                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {dept.date_created ? fDate(dept.date_created) : '—'}
                                </Typography>
                              </TableCell>

                              <TableCell align="right">
                                <IconButton size="large" color="inherit" onClick={() => handleEditClick(dept)} title="Edit">
                                  <EditIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Alert severity="info">
                                {searchTerm
                                  ? 'No departments match your search.'
                                  : 'No departments yet. Click "Add Department" to create one.'}
                              </Alert>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  )}
                </TableContainer>
              </Scrollbar>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={pagination?.count || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              />
            </CardContent>
          </Card>
        </Stack>

        <DepartmentForm open={openAddModal} onClose={() => setOpenAddModal(false)} />
        <DepartmentEditForm open={openEditModal} onClose={handleCloseEditModal} department={selectedDepartment} />
      </Container>
    </Page>
  );
}
